import React from "react"
import { Grid, NumberInput, Select, Switch, Group, ActionIcon, Text, Stack, Radio, Box } from "@mantine/core"
import { GenericFieldProps } from "src/utils/forms"
import { DateInput } from "@mantine/dates"
import { daysOfWeek, getFrequencySuffix, getMonthlyOptions, mapRecurrenceToMutation, MonthlyOption, RecurrenceObject, RecurringConfigValidationErrors, SeriesObject } from "src/utils/series"
import { currentDayCode } from "src/utils/series"
import dayjs from "dayjs"

export const RecurringBookingConfig: React.FC<GenericFieldProps> = ({ name, label, onChange, value, error, required, disabled, classNames, getValues, setValues, checkSubsAreValid }) => {

    const [date] = React.useState<string>(getValues?.['date'] || '')
    const [isRecurring, setIsRecurring] = React.useState(Boolean(value))
    const [everyN, setEveryN] = React.useState(1)
    const [frequency, setFrequency] = React.useState<'daily' | 'weekly' | 'monthly' | 'yearly'>('daily')
    const [selectedDays, setSelectedDays] = React.useState<string[]>([])
    const [endsOn, setEndsOn] = React.useState<'never' | 'date' | 'count'>('count')
    const [endDate, setEndDate] = React.useState<string | null>(null);
    const [duration, setDuration] = React.useState<number | undefined>(1);
    const [monthlyPattern, setMonthlyPattern] = React.useState<'day' | 'weekday' | null>(null);
    const [monthlyOption, setMonthlyOption] = React.useState<MonthlyOption | null>(null);
    const [seriesObject, setSeriesObject] = React.useState<SeriesObject | null>(null);
    const [validationErrors, setValidationErrors] = React.useState<RecurringConfigValidationErrors>({});

    const toggleDay = (dayValue: string) => {
        setSelectedDays(prev =>
            prev.includes(dayValue)
                ? prev.filter(day => day !== dayValue)
                : [...prev, dayValue]
        )
    }
    const monthlyOptions = getMonthlyOptions(date)

    React.useEffect(() => {
        onChange(isRecurring.toString())
    }, [isRecurring])

    React.useEffect(() => {
        if (getValues?.['date']?.length > 0) {
            let dayCode = currentDayCode(getValues?.['date'])
            setSelectedDays([dayCode])
        }        
    }, [getValues?.['date']])

    // reset endDate and duration when endsOn changes
    React.useEffect(() => {
        setEndDate(null)
        setDuration(1)
    }, [endsOn])

    React.useEffect(() => {
        if (seriesObject&&setValues) {
            Object.keys(seriesObject).forEach(key => {
                setValues(key, seriesObject[key as keyof SeriesObject])
            })
        }
    }, [seriesObject])

    React.useEffect(() => {

        var recurConfig: RecurrenceObject | null = null
        var validationErrors: RecurringConfigValidationErrors = {};

        if (endsOn === 'date' && !endDate) {
            validationErrors.endDate = 'End Date is required';
        }
        if (endsOn === 'count' && !duration) {
            validationErrors.duration = 'Duration is required';
        }

        switch (frequency) {
            case 'daily':
                recurConfig = {
                    frequency,
                    interval: everyN,
                    endDate: endsOn === 'date' ? endDate : null,
                    count: endsOn === 'count' ? duration : null
                }
                break
            case 'weekly':
                recurConfig = {
                    frequency,
                    interval: everyN,
                    endDate: endsOn === 'date' ? endDate : null,
                    count: endsOn === 'count' ? duration : null,
                    by_day: selectedDays
                }
                if (selectedDays.length === 0) {
                    validationErrors.selectedDays = 'At least one day is required';
                }
                break
            case 'monthly':
                recurConfig = {
                    frequency,
                    interval: everyN,
                    endDate: endsOn === 'date' ? endDate : null,
                    count: endsOn === 'count' ? duration : null,
                    monthlyPattern,
                    monthlyOption
                }
                if (!monthlyPattern) {
                    validationErrors.monthlyPattern = 'Monthly pattern is required';
                }
                break
            case 'yearly':
                recurConfig = {
                    frequency,
                    interval: everyN,
                    endDate: endsOn === 'date' ? endDate : null,
                    count: endsOn === 'count' ? duration : null,
                }
                break
        }

        let seriesObject = mapRecurrenceToMutation(recurConfig)
        setSeriesObject(seriesObject)

        if (checkSubsAreValid) {
            if (Object.keys(validationErrors).length > 0) {
                checkSubsAreValid(false)
            } else {
                checkSubsAreValid(true)
            }
        }

        setValidationErrors(validationErrors)

    }, [frequency, everyN, endDate, duration, selectedDays, monthlyPattern, monthlyOption, endsOn])

    const renderSeriesUI = () => {
        return (
            <React.Fragment>

                <Grid.Col span={6}>

                    <Select
                        label="Repeat Frequency"
                        placeholder="Select..."
                        value={frequency}
                        onChange={(value) => {
                            setFrequency(value as 'daily' | 'weekly' | 'monthly' | 'yearly')
                        }}
                        data={[
                            { label: 'Daily', value: 'daily' },
                            { label: 'Weekly', value: 'weekly' },
                            { label: 'Monthly', value: 'monthly' },
                            // { label: 'Yearly', value: 'yearly' }
                        ]}
                        classNames={classNames}
                    />

                </Grid.Col>

                <Grid.Col span={6}>
                    <NumberInput
                        label="Repeat Every"
                        name="interval"
                        onChange={(value) => {
                            setEveryN(value as number)
                        }}
                        value={everyN}
                        required={true}
                        defaultValue={1}
                        min={1}
                        step={1}
                        stepHoldDelay={500}
                        stepHoldInterval={100}
                        onFocus={(event) => event.target.select()}
                        classNames={classNames}
                        suffix={getFrequencySuffix(frequency, everyN)}
                    />
                </Grid.Col>

                {frequency === 'weekly' && (
                    <Grid.Col span={12}>
                        <Box mb="20">
                            <Group gap="4">
                                <Text size="sm" fw="500" mb="xs">Repeat On </Text>
                                <Text size="sm" fw="500" mb="xs" c="red">*</Text>
                            </Group>
                            <Group gap="xs" mb="xs">
                                {daysOfWeek.map((day) => (
                                    <ActionIcon
                                        key={day.value}
                                        variant={selectedDays.includes(day.value) ? "filled" : "outline"}
                                        color={selectedDays.includes(day.value) ? "blue" : "gray"}
                                        size="md"
                                        radius="xl"
                                        onClick={() => toggleDay(day.value)}
                                        disabled={disabled}
                                        aria-label={`Select ${day.fullName}`}
                                        style={{
                                            minWidth: '40px',
                                            height: '40px',
                                            fontSize: '14px',
                                            fontWeight: 500
                                        }}
                                    >
                                        {day.label}
                                    </ActionIcon>
                                ))}
                            </Group>
                            {validationErrors.selectedDays && (
                                <Text size="xs" c="red">{validationErrors.selectedDays}</Text>
                            )}
                        </Box>
                    </Grid.Col>
                )}

                {frequency === 'monthly' && (
                    <Grid.Col span={12}>
                        <Box>
                            <Select
                                label="Monthly Pattern"
                                placeholder="Repeat Monthly On"
                                value={monthlyPattern}
                                onChange={(value) => {
                                    setMonthlyPattern(value as 'day' | 'weekday');
                                    setMonthlyOption(monthlyOptions.find(option => option.value === value) || null)
                                }}
                                required={true}
                                data={monthlyOptions}
                                classNames={classNames}
                                disabled={disabled}
                                error={validationErrors.monthlyPattern}
                            />
                        </Box>
                    </Grid.Col>
                )}

                <Grid.Col span={12}>
                    <Box mb="20">
                        <Stack>
                            <Radio.Group
                                value={endsOn}
                                onChange={(value) => setEndsOn(value as 'never' | 'date' | 'count')}
                                name="endsOn"
                                label="Ends On"
                                withAsterisk
                                classNames={classNames}
                            >
                                <Group>
                                    {/* <Radio value="never" label="Never" /> */}
                                    <Radio value="date" label="Date" />
                                    <Radio value="count" label="Count" />
                                </Group>
                            </Radio.Group>
                        </Stack>
                    </Box>
                </Grid.Col>

                {endsOn === 'date' && (
                    <Grid.Col span={12}>
                        <Box mb="20">
                            <DateInput
                                label="End Date"
                                value={endDate}
                                onChange={(value) => {
                                    if (typeof value === 'string') {
                                        setEndDate(value)
                                    }
                                    if (value === null) {
                                        setEndDate('')
                                    }
                                    if (typeof value === 'object' && value !== null) {
                                        let dobj = value as any
                                        setEndDate(dobj.toISOString())
                                    }
                                }}
                                clearable
                                classNames={classNames}
                                dateParser={(dateString) => new Date(Date.parse(dateString))}
                                error={validationErrors.endDate}
                                maxDate={dayjs().add(9, 'weeks').toDate()}
                            />
                        </Box>
                    </Grid.Col>
                )}

                {endsOn === 'count' && (
                    <Grid.Col span={12}>
                        <NumberInput
                            label="End After"
                            name="duration"
                            onChange={(value) => {
                                setDuration(value as number)
                            }}
                            value={duration}
                            error={validationErrors.duration}
                            required={true}
                            placeholder="Number of occurrences"
                            min={1}
                            step={1}
                            stepHoldDelay={500}
                            stepHoldInterval={100}
                            onFocus={(event) => event.target.select()}
                            classNames={classNames}
                            suffix=" occurrences"
                        />
                    </Grid.Col>
                )}

            </React.Fragment>
        )
    }

    return (
        <Grid gutter="xs" m={0} mb={20}>

            <Grid.Col span={12}>

                <Switch
                    label={label}
                    size="md"
                    name={name}
                    onChange={(e) => {
                        setIsRecurring(!!e.currentTarget.checked)
                    }}
                    checked={isRecurring}
                    required={required}
                    disabled={disabled}
                    error={error}
                    classNames={classNames}
                    mb={20}
                />

            </Grid.Col>

            {isRecurring && renderSeriesUI()}


        </Grid>
    )

}